/**
 * RecurrencePicker — builds a shared `Recurrence` value for chore templates.
 *
 * Covers every variant the shared Zod schema accepts (Fase 2 added `interval`
 * to daily/weekly for "annenhver"):
 *   • once     — engangs
 *   • daily    — hver / annenhver / hver N. dag        (interval 1..30)
 *   • weekdays — utvalgte ukedager (man–søn, ISO 1..7)
 *   • weekly   — ukedag N, hver / annenhver / hver N. uke (weekday + interval)
 *   • monthly  — dag i måneden (1..31)
 *
 * Controlled: the parent owns the `Recurrence` value and re-renders on change.
 * The picker keeps a tiny bit of local memory for the per-variant sub-fields so
 * switching type and back doesn't lose the user's day/interval choices.
 *
 * Week starts Monday (nb-NO / week-start-Monday constraint). All copy via t().
 *
 * `variant="chips"` er Foreslå-arkets utgave (#154, steg 4c, D12): fire chips
 * — Én gang, Hver dag, Hver uke, Utvalgte dager — i stedet for nedtrekkslisten,
 * og Stepper i stedet for tallfeltene. `monthly` ligger under «Utvalgte dager»
 * sammen med ukedagene, så alle fem typene og intervallene er fortsatt mulige
 * å velge. Admins malskjema bruker fortsatt standardutgaven: skjemaet er ikke
 * tegnet ennå (PLAN.md «Not designed yet»), så i steg 9b (#159) fikk det bare
 * de nye tokenene, med `field` på feltene.
 */
import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Recurrence } from '@stell/shared';
import { Chip, SegmentedTabs, Stepper } from '../../components/primitives';

export interface RecurrencePickerProps {
  value: Recurrence;
  onChange: (next: Recurrence) => void;
  /** `form` (standard): nedtrekksliste og tallfelt. `chips`: Foreslå-arket. */
  variant?: 'form' | 'chips';
  /** Overskriften i `chips`-utgaven; standard er recurrence.label. */
  label?: string;
}

/** De fire chipsene i `chips`-utgaven. `selected` dekker weekdays og monthly. */
type ChipKind = 'once' | 'daily' | 'weekly' | 'selected';
const CHIP_KINDS: readonly ChipKind[] = ['once', 'daily', 'weekly', 'selected'];

function chipKindOf(type: Recurrence['type']): ChipKind {
  return type === 'weekdays' || type === 'monthly' ? 'selected' : type;
}

/** Etiketten over en underkontroll i `chips`-utgaven. */
const SUB_LABEL = 'text-sm font-semibold text-ink-2';

/** ISO weekday order Mon(1)…Sun(7) — week starts Monday. */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export function RecurrencePicker({
  value,
  onChange,
  variant = 'form',
  label,
}: RecurrencePickerProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();

  // Remember sub-field choices across type switches so toggling type and back
  // restores the user's last day/interval rather than resetting to defaults.
  const [days, setDays] = useState<number[]>(
    value.type === 'weekdays' ? value.days : [1],
  );
  const [weekday, setWeekday] = useState<number>(
    value.type === 'weekly' ? value.weekday : 1,
  );
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    value.type === 'monthly' ? value.dayOfMonth : 1,
  );
  const [dailyInterval, setDailyInterval] = useState<number>(
    value.type === 'daily' && value.interval ? value.interval : 1,
  );
  const [weeklyInterval, setWeeklyInterval] = useState<number>(
    value.type === 'weekly' && value.interval ? value.interval : 1,
  );

  function onTypeChange(type: Recurrence['type']): void {
    switch (type) {
      case 'once':
        onChange({ type: 'once' });
        break;
      case 'daily':
        onChange({ type: 'daily', interval: dailyInterval });
        break;
      case 'weekdays':
        onChange({ type: 'weekdays', days: days.length > 0 ? days : [1] });
        break;
      case 'weekly':
        onChange({ type: 'weekly', weekday, interval: weeklyInterval });
        break;
      case 'monthly':
        onChange({ type: 'monthly', dayOfMonth });
        break;
    }
  }

  function toggleDay(d: number): void {
    const next = days.includes(d)
      ? days.filter((x) => x !== d)
      : [...days, d].sort((a, b) => a - b);
    // weekdays must have at least one day — ignore a toggle that empties it.
    if (next.length === 0) return;
    setDays(next);
    onChange({ type: 'weekdays', days: next });
  }

  if (variant === 'chips') {
    const kind = chipKindOf(value.type);
    const onChip = (k: ChipKind): void => {
      if (k === kind) return;
      // «Utvalgte dager» åpner på ukedagene; månedlig er ett trykk til.
      onTypeChange(k === 'selected' ? 'weekdays' : k);
    };
    return (
      <div
        role="group"
        aria-labelledby={headingId}
        className="flex flex-col gap-3"
        data-testid="recurrence-picker"
      >
        <span
          id={headingId}
          className="m-0 block text-caption font-bold tracking-wider text-ink-2 uppercase"
        >
          {label ?? t('recurrence.label')}
        </span>
        <div className="flex flex-wrap gap-2">
          {CHIP_KINDS.map((k) => (
            <Chip
              key={k}
              selected={kind === k}
              data-testid={`recurrence-chip-${k}`}
              onClick={() => onChip(k)}
            >
              {t(`recurrence.chips.${k}`)}
            </Chip>
          ))}
        </div>

        {value.type === 'daily' ? (
          <div className="flex items-center justify-between gap-3">
            <span className={SUB_LABEL}>{t('recurrence.everyNDays')}</span>
            <Stepper
              size="md"
              label={t('recurrence.everyNDays')}
              value={value.interval ?? 1}
              min={1}
              max={30}
              data-testid="recurrence-daily-interval"
              onChange={(n) => {
                setDailyInterval(n);
                onChange({ type: 'daily', interval: n });
              }}
            />
          </div>
        ) : null}

        {value.type === 'weekly' ? (
          <div className="flex flex-col gap-3" data-testid="recurrence-weekly">
            <div role="group" aria-label={t('recurrence.weekday')} className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d) => (
                <Chip
                  key={d}
                  selected={value.weekday === d}
                  data-testid={`recurrence-weekly-day-${d}`}
                  onClick={() => {
                    setWeekday(d);
                    onChange({ type: 'weekly', weekday: d, interval: value.interval ?? 1 });
                  }}
                >
                  {t(`recurrence.weekdayShort.${d}`)}
                </Chip>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className={SUB_LABEL}>{t('recurrence.everyNWeeks')}</span>
              <Stepper
                size="md"
                label={t('recurrence.everyNWeeks')}
                value={value.interval ?? 1}
                min={1}
                max={30}
                data-testid="recurrence-weekly-interval"
                onChange={(n) => {
                  setWeeklyInterval(n);
                  onChange({ type: 'weekly', weekday: value.weekday, interval: n });
                }}
              />
            </div>
          </div>
        ) : null}

        {kind === 'selected' ? (
          <div className="flex flex-col gap-3">
            <SegmentedTabs
              label={t('recurrence.chips.selected')}
              value={value.type === 'monthly' ? 'monthly' : 'weekdays'}
              onChange={(tp) => onTypeChange(tp)}
              options={[
                { value: 'weekdays', label: t('recurrence.selected.weekdays'), testId: 'recurrence-selected-weekdays' },
                { value: 'monthly', label: t('recurrence.selected.monthly'), testId: 'recurrence-selected-monthly' },
              ]}
            />
            {value.type === 'weekdays' ? (
              <div
                role="group"
                aria-label={t('recurrence.pickDays')}
                className="flex flex-wrap gap-2"
                data-testid="recurrence-weekdays"
              >
                {WEEKDAYS.map((d) => (
                  <Chip
                    key={d}
                    selected={value.days.includes(d)}
                    data-testid={`recurrence-day-${d}`}
                      onClick={() => toggleDay(d)}
                  >
                    {t(`recurrence.weekdayShort.${d}`)}
                  </Chip>
                ))}
              </div>
            ) : null}
            {value.type === 'monthly' ? (
              <div className="flex items-center justify-between gap-3">
                <span className={SUB_LABEL}>{t('recurrence.dayOfMonth')}</span>
                <Stepper
                  size="md"
                  label={t('recurrence.dayOfMonth')}
                  value={value.dayOfMonth}
                  min={1}
                  max={31}
                  data-testid="recurrence-day-of-month"
                  onChange={(n) => {
                    setDayOfMonth(n);
                    onChange({ type: 'monthly', dayOfMonth: n });
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <fieldset className="flex flex-col gap-3" data-testid="recurrence-picker">
      <legend className="text-ink">{t('recurrence.label')}</legend>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-ink-2">{t('recurrence.type')}</span>
        <select
          data-testid="recurrence-type"
          className="field"
          value={value.type}
          onChange={(e) => onTypeChange(e.target.value as Recurrence['type'])}
        >
          {(['once', 'daily', 'weekdays', 'weekly', 'monthly'] as const).map((tp) => (
            <option key={tp} value={tp}>
              {t(`recurrence.types.${tp}`)}
            </option>
          ))}
        </select>
      </label>

      {/* daily — interval */}
      {value.type === 'daily' ? (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-ink-2">{t('recurrence.everyNDays')}</span>
          <input
            data-testid="recurrence-daily-interval"
            type="number"
            min={1}
            max={30}
            className="field"
            value={value.interval ?? 1}
            onChange={(e) => {
              const n = Math.min(30, Math.max(1, Number(e.target.value) || 1));
              setDailyInterval(n);
              onChange({ type: 'daily', interval: n });
            }}
          />
          <span className="text-caption text-ink-2">{t('recurrence.intervalHint')}</span>
        </label>
      ) : null}

      {/* weekdays — multi day pick */}
      {value.type === 'weekdays' ? (
        <div className="flex flex-col gap-1" data-testid="recurrence-weekdays">
          <span className="text-sm text-ink-2">{t('recurrence.pickDays')}</span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => {
              const on = value.days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  data-testid={`recurrence-day-${d}`}
                  aria-pressed={on}
                  onClick={() => toggleDay(d)}
                  className={[
                    'min-h-11 rounded-pill px-3.5 text-sm font-bold',
                    on ? 'bg-solid text-on-solid' : 'border border-secondary-line bg-secondary text-ink',
                  ].join(' ')}
                >
                  {t(`recurrence.weekdayShort.${d}`)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* weekly — weekday + interval */}
      {value.type === 'weekly' ? (
        <div className="flex flex-col gap-3" data-testid="recurrence-weekly">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-ink-2">{t('recurrence.weekday')}</span>
            <select
              data-testid="recurrence-weekly-weekday"
              className="field"
              value={value.weekday}
              onChange={(e) => {
                const wd = Number(e.target.value);
                setWeekday(wd);
                onChange({ type: 'weekly', weekday: wd, interval: value.interval ?? 1 });
              }}
            >
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {t(`recurrence.weekdayLong.${d}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-ink-2">{t('recurrence.everyNWeeks')}</span>
            <input
              data-testid="recurrence-weekly-interval"
              type="number"
              min={1}
              max={30}
              className="field"
              value={value.interval ?? 1}
              onChange={(e) => {
                const n = Math.min(30, Math.max(1, Number(e.target.value) || 1));
                setWeeklyInterval(n);
                onChange({ type: 'weekly', weekday: value.weekday, interval: n });
              }}
            />
            <span className="text-caption text-ink-2">{t('recurrence.intervalHint')}</span>
          </label>
        </div>
      ) : null}

      {/* monthly — day of month */}
      {value.type === 'monthly' ? (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-ink-2">{t('recurrence.dayOfMonth')}</span>
          <input
            data-testid="recurrence-day-of-month"
            type="number"
            min={1}
            max={31}
            className="field"
            value={value.dayOfMonth}
            onChange={(e) => {
              const n = Math.min(31, Math.max(1, Number(e.target.value) || 1));
              setDayOfMonth(n);
              onChange({ type: 'monthly', dayOfMonth: n });
            }}
          />
        </label>
      ) : null}
    </fieldset>
  );
}
